import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  AlertCircle,
  MapPin,
  X,
  PlayCircle,
  XCircle,
  UserPlus,
  Video,
} from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../hooks/useAuth";
import { AppointmentStatusBadge } from "./components/AppointmentStatusBadge";
import { ReassignModal } from "../dashboard/receptionist/modals/ReassignModal";


export const AppointmentDetails = ({ appointmentId, onClose }) => {
  const { get, post, put } = useApi();
  const { user } = useAuth();
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);

  // Use either the prop appointmentId or the URL param id
  const id = appointmentId || urlId;

  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      try {
        setLoading(true);
        const response = await get(`/appointments/${id}`);
        if (response.data?.status === "success") {
          setAppointment(response.data.data);
        } else {
          setError("Failed to load appointment details");
        }
      } catch (err) {
        console.error("Error fetching appointment:", err);
        setError(
          err.response?.data?.message || "Failed to load appointment details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAppointmentDetails();
    }
  }, [id, get]);

  const handleStartSession = async () => {
    try {
      setIsStartingSession(true);
      setError(null);

      // Check if this is a virtual session
      if (appointment.sessionType === 'virtual') {
        // For virtual sessions, navigate directly to the video interface
        // Update appointment status first
        await put(
          `/appointments/${appointment._id}/status`,
          { status: 'in-progress' }
        );

        // Navigate to virtual session room
        navigate(`/${user.role}/virtual-session/${appointment._id}`);
        return;
      }

      // For physical sessions, continue with the existing flow
      // First update appointment status
      const statusResponse = await put(
        `/appointments/${appointment._id}/status`,
        { status: 'in-progress' }
      );

      if (statusResponse.data?.status === 'success') {
        // Initialize the session
        const sessionResponse = await post(
          `/sessions/${appointment._id}/initialize`
        );

        if (sessionResponse.data?.status === 'success') {
          // Use the appropriate route based on user role
          if (user.role === 'hod') {
            navigate(`/hod/sessions/${appointment._id}`);
          } else {
            navigate(`/counselor/sessions/${appointment._id}`);
          }
        } else {
          throw new Error('Failed to initialize session');
        }
      } else {
        throw new Error('Failed to update appointment status');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      setError(error.response?.data?.message || 'Failed to start session');
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) {
      return;
    }

    try {
      await post(`/appointments/${id}/cancel`);
      if (onClose) {
        onClose();
      } else {
        navigate("/appointments");
      }
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
      setError("Failed to cancel appointment. Please try again.");
    }
  };

  const handleReassign = async (appointmentId, reassignData) => {
    try {
      setError(null);
      const response = await put(`/appointments/${appointmentId}/reassign`, reassignData);

      if (response.data?.status === "success") {
        setAppointment(response.data.data);
        setShowReassignModal(false);
      } else {
        throw new Error("Failed to reassign appointment");
      }
    } catch (error) {
      console.error("Failed to reassign appointment:", error);
      setError(error.response?.data?.message || "Failed to reassign appointment");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Appointment not found</p>
      </div>
    );
  }

  const canStartSession =
    (user?.role === "counselor") &&
    appointment?.status === "scheduled" &&
    appointment?.counselor?._id === user?._id;

  const canCancelAppointment =
    user.role === "student" ||
    user.role === "counselor" || user.role === "staff" ||
    user.role === "receptionist";

  const canReschedule =
    (user.role === "student" && appointment.client._id === user._id) ||
    user.role === "receptionist" || user.role === "staff";

  const canReassign =
    user.role === "receptionist" ||
    user.role === "admin" ||
    user.role === "staff";

  // Check if user can join virtual session
  const canJoinVirtualSession = () => {
    if (appointment.sessionType !== 'virtual') return false;
    if (!['scheduled', 'in-progress'].includes(appointment.status)) return false;

    const isParticipant =
      appointment.client._id === user._id ||
      appointment.counselor._id === user._id;

    if (!isParticipant) return false;

    // Check if within 15 minutes of appointment time
    const appointmentDateTime = new Date(appointment.date);
    const [hours, minutes] = appointment.timeSlot.split(':');
    appointmentDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    const fifteenMinutesBefore = new Date(appointmentDateTime);
    fifteenMinutesBefore.setMinutes(fifteenMinutesBefore.getMinutes() - 15);

    const now = new Date();
    return now >= fifteenMinutesBefore;
  };

  const handleJoinVirtualSession = () => {
    navigate(`/${user.role}/virtual-session/${appointment._id}`);
  };

  // Determine if this is being used as a modal or full page
  const isModal = !!onClose;

  // If used as a modal, render compact version
  if (isModal) {
    return (
      <div className="bg-white rounded-lg shadow-xl max-w-4xl mx-auto max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">Appointment Details</h2>
            <p className="text-blue-100 text-sm mt-1">
              {new Date(appointment.date).toLocaleDateString()} at {appointment.timeSlot}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <AppointmentStatusBadge status={appointment.status} />
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Quick Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(user.role === "counselor" || user.role === "admin" || user.role === "receptionist") && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h3 className="text-xs font-medium text-gray-500 mb-1">Client</h3>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {appointment.client?.fullName || appointment.client?.username}
                  </p>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="text-xs font-medium text-gray-500 mb-1">Counselor</h3>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {appointment.counselor?.fullName || appointment.counselor?.username}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="text-xs font-medium text-gray-500 mb-1">Service</h3>
                <p className="text-sm font-semibold text-gray-900">{appointment.serviceType}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="text-xs font-medium text-gray-500 mb-1">Type</h3>
                <p className="text-sm font-semibold text-gray-900 capitalize">{appointment.sessionType}</p>
              </div>
            </div>

            {/* Client Details */}
            <div className="border-t pt-4">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Client Information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium">{appointment.client?.fullName}</span></div>
                <div><span className="text-gray-500">Email:</span> <span className="font-medium">{appointment.client?.email}</span></div>
                <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{appointment.client?.phone}</span></div>
                {appointment.client?.studentInfo?.studentId && (
                  <div><span className="text-gray-500">Student ID:</span> <span className="font-medium">{appointment.client.studentInfo.studentId}</span></div>
                )}
              </div>
            </div>

            {/* Notes */}
            {appointment.notes && (
              <div className="border-t pt-4">
                <h3 className="text-md font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                  {appointment.notes}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer with Actions */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex flex-wrap gap-2 justify-end">
            {canJoinVirtualSession() && (
              <button
                onClick={handleJoinVirtualSession}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center animate-pulse shadow-md"
              >
                <Video className="h-4 w-4 mr-2" />
                Join Virtual Session
              </button>
            )}
            {canStartSession && (
              <button
                onClick={handleStartSession}
                disabled={isStartingSession}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:bg-green-400"
              >
                {isStartingSession ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Starting...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Start Session
                  </>
                )}
              </button>
            )}
            {canReschedule && (
              <button
                onClick={() => navigate(`/${user.role}/appointments/${appointment._id}/reschedule`)}
                className="px-4 py-2 bg-uzima-maroon-500 text-white rounded-lg hover:bg-uzima-maroon-600"
              >
                Reschedule
              </button>
            )}
            {canReassign && (
              <button
                onClick={() => setShowReassignModal(true)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Reassign
              </button>
            )}
            {canCancelAppointment && (
              <button
                onClick={handleCancelAppointment}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>

        {/* Reassign Modal */}
        <ReassignModal
          isOpen={showReassignModal}
          onClose={() => setShowReassignModal(false)}
          appointment={appointment}
          onReassign={handleReassign}
        />
      </div>
    );
  }

  // Full page version
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Header with Gradient */}
      <div className="bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Appointment Details</h1>
                <p className="text-blue-100 text-sm mt-1">
                  {new Date(appointment.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} at {appointment.timeSlot}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <AppointmentStatusBadge status={appointment.status} />
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Appointment Info Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Client Info (visible to counselors and admin) */}
                {(user.role === "counselor" ||
                  user.role === "admin" ||
                  user.role === "receptionist") && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Client</h3>
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-2" />
                        <p className="text-gray-900">
                          {appointment.client?.fullName ||
                            appointment.client?.username}
                        </p>
                      </div>
                    </div>
                  )}

                {/* Counselor */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Counselor</h3>
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-gray-400 mr-2" />
                    <p className="text-gray-900">
                      {appointment.counselor?.fullName ||
                        appointment.counselor?.username}
                    </p>
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Service</h3>
                  <p className="text-gray-900">{appointment.serviceType}</p>
                </div>

                {/* Session Type */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Session Type</h3>
                  <p className="text-gray-900 capitalize">{appointment.sessionType}</p>
                </div>
              </div>
            </div>

            {/* Client Details Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><strong className="text-sm font-medium text-gray-500">Full Name:</strong> {appointment.client?.fullName}</div>
                <div><strong className="text-sm font-medium text-gray-500">Email:</strong> {appointment.client?.email}</div>
                <div><strong className="text-sm font-medium text-gray-500">Username:</strong> {appointment.client?.username}</div>
                <div><strong className="text-sm font-medium text-gray-500">Phone Number:</strong> {appointment.client?.phone}</div>
              </div>

              {/* Student Info */}
              {appointment.client?.studentInfo && (
                <>
                  {/* Convert the intake semesters to the actual values */}
                  <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Student Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><strong className="text-sm font-medium text-gray-500">Student ID:</strong> {appointment.client.studentInfo.studentId}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Enrollment Year:</strong> {appointment.client.studentInfo.enrollmentYear}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Intake Semester:</strong> {appointment.client.studentInfo.intakeSemester}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Year of Study:</strong> {appointment.client.studentInfo.yearOfStudy}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Course Taken:</strong> {appointment.client.studentInfo.courseTaken}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Gender:</strong> {appointment.client.studentInfo.profile?.gender}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Date of Birth:</strong> {new Date(appointment.client.studentInfo.profile?.dateOfBirth).toLocaleDateString()}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Nationality:</strong> {appointment.client.studentInfo.profile?.nationality}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Religion:</strong> {appointment.client.studentInfo.profile?.religion}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Residence:</strong> {appointment.client.studentInfo.profile?.residence}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Marital Status:</strong> {appointment.client.studentInfo.profile?.maritalStatus}</div>
                  </div>

                  <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Next of Kin</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><strong className="text-sm font-medium text-gray-500">Name:</strong> {appointment.client.studentInfo.nextOfKin?.name}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Relation:</strong> {appointment.client.studentInfo.nextOfKin?.relation}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Email:</strong> {appointment.client.studentInfo.nextOfKin?.email}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Phone:</strong> {appointment.client.studentInfo.nextOfKin?.phone}</div>
                  </div>
                </>
              )}

              {/* Staff Info */}
              {appointment.client?.staffInfo && (
                <>
                  <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Staff Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><strong className="text-sm font-medium text-gray-500">Staff ID:</strong> {appointment.client.staffInfo.staffId}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Department:</strong> {appointment.client.staffInfo.department}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Position:</strong> {appointment.client.staffInfo.position}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Gender:</strong> {appointment.client.staffInfo.profile?.gender}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Date of Birth:</strong> {new Date(appointment.client.staffInfo.profile?.dateOfBirth).toLocaleDateString()}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Nationality:</strong> {appointment.client.staffInfo.profile?.nationality}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Religion:</strong> {appointment.client.staffInfo.profile?.religion}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Residence:</strong> {appointment.client.staffInfo.profile?.residence}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Marital Status:</strong> {appointment.client.staffInfo.profile?.maritalStatus}</div>
                  </div>

                  <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Next of Kin</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><strong className="text-sm font-medium text-gray-500">Name:</strong> {appointment.client.staffInfo.nextOfKin?.name}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Relation:</strong> {appointment.client.staffInfo.nextOfKin?.relation}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Email:</strong> {appointment.client.staffInfo.nextOfKin?.email}</div>
                    <div><strong className="text-sm font-medium text-gray-500">Phone:</strong> {appointment.client.staffInfo.nextOfKin?.phone}</div>
                  </div>
                </>
              )}
            </div>

            {/* Date & Time Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Schedule</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Date</h3>
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                    <p className="text-gray-900">
                      {new Date(appointment.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Time</h3>
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-gray-400 mr-2" />
                    <p className="text-gray-900">{appointment.timeSlot}</p>
                  </div>
                </div>
              </div>

              {/* Location */}
              {appointment.location && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Location</h3>
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                    <p className="text-gray-900">{appointment.location}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Notes Card */}
            {appointment.notes && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Notes</h2>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {appointment.notes}
                </p>
              </div>
            )}
          </div>

          {/* Right Sidebar - Actions (1/3) */}
          <div className="space-y-6">
            {/* Actions Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
              <div className="space-y-3">
                {canJoinVirtualSession() && (
                  <button
                    onClick={handleJoinVirtualSession}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center animate-pulse shadow-lg"
                  >
                    <Video className="h-5 w-5 mr-2" />
                    Join Virtual Session
                  </button>
                )}

                {canStartSession && (
                  <button
                    onClick={handleStartSession}
                    disabled={isStartingSession}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center disabled:bg-green-400 shadow-md"
                  >
                    {isStartingSession ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <PlayCircle className="h-5 w-5 mr-2" />
                        Start Session
                      </>
                    )}
                  </button>
                )}

                {canReschedule && (
                  <button
                    onClick={() => navigate(`/${user.role}/appointments/${appointment._id}/reschedule`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Reschedule
                  </button>
                )}

                {canReassign && (
                  <button
                    onClick={() => setShowReassignModal(true)}
                    className="w-full px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center justify-center shadow-md"
                  >
                    <UserPlus className="h-5 w-5 mr-2" />
                    Reassign
                  </button>
                )}

                {canCancelAppointment && (
                  <button
                    onClick={handleCancelAppointment}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center shadow-md"
                  >
                    <XCircle className="h-5 w-5 mr-2" />
                    Cancel
                  </button>
                )}

                {onClose && (
                  <button
                    onClick={onClose}
                    className="w-full px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>

            {/* Student ID Card */}
            {appointment.client?.studentInfo?.studentId && (
              <div className="bg-gradient-to-br from-uzima-maroon-50 to-blue-50 rounded-lg shadow-md p-6 border border-uzima-maroon-100">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Student ID</h3>
                <p className="text-2xl font-bold text-uzima-maroon-600">
                  {appointment.client.studentInfo.studentId}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reassign Modal */}
      <ReassignModal
        isOpen={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        appointment={appointment}
        onReassign={handleReassign}
      />
    </div>
  );
};

export default AppointmentDetails;

